"use client";

import React from "react";
import {
  Group,
  Pagination,
  Flex,
  Text,
  type PaginationProps,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { CustomSelect } from "@components/custom/form-fields/custom-select";
import styles from "@scss/custom-components/pagination.module.scss";

export interface CustomPaginationProps extends PaginationProps {
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  pageSizeLabel?: string;
  pageSizeOptions?: number[];
  paddingX?: number | string;
  selectVariant?: "default" | "dark" | "darker" | "sky";
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
  pageSize = 50,
  onPageSizeChange,
  showPageSizeSelector = true,
  pageSizeLabel = "Results per page:",
  pageSizeOptions,
  selectVariant,
  className,
  paddingX,
  ...paginationProps
}) => {
  const isMobile = useMediaQuery("(max-width: 75em)");

  const handlePageSizeChange = (value: string | null) => {
    if (value && onPageSizeChange) {
      onPageSizeChange(parseInt(value, 10));
    }
  };

  const pageSizeSelectData = Array.isArray(pageSizeOptions)
    ? pageSizeOptions
        .filter((option) => typeof option === "number" && !isNaN(option))
        .map((option) => ({
          value: option.toString(),
          label: option.toString(),
        }))
    : [];

  return (
    <Flex
      justify={showPageSizeSelector ? "space-between" : "flex-end"}
      align="flex-end"
      className={`${styles.paginationWrapper} ${className || ""}`}
      px={paddingX}
    >
      {showPageSizeSelector && (
        <Group align="center" gap="xs" className={styles.pageSizeSelector}>
          <Text size="sm" className="grey300">
            {pageSizeLabel}
          </Text>
          <CustomSelect
            value={pageSize.toString()}
            variant={selectVariant}
            onChange={handlePageSizeChange}
            data={pageSizeSelectData}
            className={styles.pageSizeSelect}
            size="sm"
            w={50}
            allowDeselect={false}
            comboboxProps={{
              withinPortal: false,
            }}
          />
        </Group>
      )}
      <Pagination {...paginationProps} withPages={!isMobile} />
    </Flex>
  );
};
